import { useEffect, useMemo, useState } from 'react';

import { configApi } from '../services/configApi';
import type { Place, StorageEntityInfo } from '../types/warehouse.types';

/**
 * Hook for loading storage entities based on a list of selected places.
 * @param allSelectedPlaces - An array of Place objects that are currently selected.
 */
export const useStorageEntityLoader = (allSelectedPlaces: Place[]) => {
  const [storageEntities, setStorageEntities] = useState<StorageEntityInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const storageEntityIds = useMemo(() => {
    const occupiedPlaces = allSelectedPlaces.filter(p => p.occupied);
    const ids = occupiedPlaces.flatMap(p => p.storage_entity_ids || []);
    return Array.from(new Set(ids)).sort();
  }, [allSelectedPlaces]);

  useEffect(() => {
    if (storageEntityIds.length === 0) {
      setStorageEntities([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchEntities = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await configApi.fetchStorageEntities(storageEntityIds, signal);
        if (!signal.aborted) {
          setStorageEntities(data);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError' && !signal.aborted) {
          console.error("Error fetching storage entities:", err);
          setError(err);
          setStorageEntities([]); 
        }
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchEntities();

    return () => {
      abortController.abort();
    };
  }, [JSON.stringify(storageEntityIds)]);

  return { storageEntities };
};
